import React, { useState, useEffect, useMemo } from 'react';
import { adminCriticalityApi } from '../services/api';
import Card from './common/Card';
import AssetRiskDashboard, { AssetRiskDistribution } from './AssetRiskDashboard';
import TopThreatsChart from './TopThreatsChart';
import AssetExposureChart from './AssetExposureChart';
import TopVulnerabilitiesChart from './TopVulnerabilitiesChart';

const CLASSIFICATION_ORDER = ['Critical', 'High', 'Moderate', 'Low', 'Info'];
const GAUGE_COLORS = {
  Info: '#94a3b8',
  Low: '#38bdf8',
  Moderate: '#facc15',
  High: '#fb923c',
  Critical: '#dc2626',
};

export default function AdminRiskDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch risk dashboard metrics which includes asset rows + aggregates
        const response = await adminCriticalityApi.getRiskDashboardMetrics();
        setMetrics(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load risk metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  const { 
    rows = [],
    top_threats = [],
    asset_exposure = [],
    top_vulnerabilities = [],
  } = metrics || {};

  // Build doughnut data from unique assets
  const doughnutData = useMemo(() => {
    if (!rows || rows.length === 0) {
      return [];
    }

    // Count unique assets by classification
    const uniqueAssets = new Set();
    const classificationCounts = {};
    const CLASSIFICATION_ORDER = ['Info', 'Low', 'Moderate', 'High', 'Critical'];
    CLASSIFICATION_ORDER.forEach((c) => {
      classificationCounts[c] = 0;
    });

    rows.forEach((row) => {
      if (!row.asset_name) return;
      const asset_key = row.asset_name;
      
      if (!uniqueAssets.has(asset_key)) {
        uniqueAssets.add(asset_key);
        const classification = row.classification || 'Info';
        classificationCounts[classification]++;
      }
    });

    const GAUGE_COLORS = {
      Info: '#94a3b8',
      Low: '#38bdf8',
      Moderate: '#facc15',
      High: '#fb923c',
      Critical: '#dc2626',
    };

    const total = uniqueAssets.size;
    return CLASSIFICATION_ORDER.map((level) => {
      const count = classificationCounts[level] || 0;
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
      return {
        label: level,
        value: count,
        percentage,
        color: GAUGE_COLORS[level],
      };
    }).filter((item) => item.value > 0);
  }, [rows]);

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-brand-500 mx-auto" />
          <p className="mt-4 text-slate-600 text-sm font-medium">Loading risk metrics...</p>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-red-600 text-sm">{error}</div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Asset Risk Distribution - Risk Matrix */}
      {/* <AssetRiskDashboard data={metrics} /> */}

      {/* Risk Metrics Grid - Doughnut, Top Threats, Asset Exposure, Top Vulnerabilities */}
      {(doughnutData.length > 0 || top_threats.length > 0 || asset_exposure.length > 0 || top_vulnerabilities.length > 0) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Asset Risk Distribution - Doughnut */}
          <AssetRiskDistribution doughnutData={doughnutData} />

          {/* Top Threats */}
          {top_threats.length > 0 && (
            <Card className="p-5">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Top Threats</h3>
              <p className="text-xs text-slate-400 mb-3">Threats with highest risk scores across all assets</p>
              <TopThreatsChart data={top_threats} />
            </Card>
          )}

          {/* Asset Exposure */}
          {asset_exposure.length > 0 && (
            <Card className="p-5">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Asset Exposure</h3>
              <p className="text-xs text-slate-400 mb-3">Assets with highest risk exposure (max risk score)</p>
              <AssetExposureChart data={asset_exposure} />
            </Card>
          )}

          {/* Top Vulnerabilities */}
          {top_vulnerabilities.length > 0 && (
            <Card className="p-5">
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">Top Vulnerabilities</h3>
              <p className="text-xs text-slate-400 mb-3">Vulnerabilities with highest risk levels</p>
              <TopVulnerabilitiesChart data={top_vulnerabilities} />
            </Card>
          )}
        </div>
      ) : null}
    </div>
  );
}
